import { useCallback, useEffect, useRef, useState } from 'react';
import { unlockAudio } from './audioEngine';
import { startRecording, transcribe } from './recorder';

const TAP_MS = 280;

/* Press-and-hold to talk: press() starts the microphone, release() stops it
   and sends the recording for transcription → onText(text). A quick tap
   (shorter than TAP_MS) keeps recording until the next press — for anyone who
   finds holding awkward.
   state: 'idle' | 'starting' | 'recording' | 'transcribing'
   level(): loudness of the microphone 0..1 (read it in an animation frame). */
export default function useHoldToTalk({ onText, onError, onStart } = {}) {
  const [state, setState] = useState('idle');
  const [isLatched, setLatched] = useState(false);
  const rec = useRef(null);
  const pressedAt = useRef(0);
  const latched = useRef(false);
  const pendingStop = useRef(false);
  const busy = useRef(false);
  const ctrl = useRef(null);
  const cb = useRef({ onText, onError, onStart });
  cb.current = { onText, onError, onStart };

  const finish = useCallback(async () => {
    const r = rec.current;
    rec.current = null;
    latched.current = false;
    setLatched(false);
    if (!r) return;
    setState('transcribing');
    try {
      const { blob, seconds, voiced } = await r.stop();
      if (seconds < 0.35 || voiced < 0.15) {
        setState('idle');
        cb.current.onText && cb.current.onText('', { empty: true });
        return;
      }
      ctrl.current = new AbortController();
      const text = await transcribe(blob, { signal: ctrl.current.signal });
      setState('idle');
      cb.current.onText && cb.current.onText(text, { empty: !text, seconds });
    } catch (e) {
      setState('idle');
      if (e.name !== 'AbortError' && cb.current.onError) cb.current.onError(e);
    }
  }, []);

  const press = useCallback(async () => {
    unlockAudio();
    if (rec.current) {
      // second tap of a tap-to-talk, or a press while still recording
      if (latched.current) finish();
      return;
    }
    if (busy.current) return;
    busy.current = true;
    pressedAt.current = performance.now();
    pendingStop.current = false;
    setState('starting');
    try {
      cb.current.onStart && cb.current.onStart();
      rec.current = await startRecording();
      setState('recording');
      if (pendingStop.current) {
        // released before the microphone even opened → treat as a tap
        latched.current = true;
        setLatched(true);
      }
    } catch (e) {
      rec.current = null;
      setState('idle');
      const msg =
        e && (e.name === 'NotAllowedError' || e.name === 'SecurityError')
          ? 'Нет доступа к микрофону — разрешите его в настройках браузера'
          : e && e.name === 'NotFoundError'
            ? 'Микрофон не найден'
            : (e && e.message) || 'Микрофон не включился';
      if (cb.current.onError) cb.current.onError(new Error(msg));
    } finally {
      busy.current = false;
    }
  }, [finish]);

  const release = useCallback(() => {
    if (!rec.current) {
      pendingStop.current = true;
      return;
    }
    if (latched.current) return;
    if (performance.now() - pressedAt.current < TAP_MS) {
      latched.current = true; // tap → keep listening until the next tap
      setLatched(true);
      return;
    }
    finish();
  }, [finish]);

  const cancel = useCallback(() => {
    if (ctrl.current) ctrl.current.abort();
    const r = rec.current;
    rec.current = null;
    latched.current = false;
    setLatched(false);
    if (r) r.cancel();
    setState('idle');
  }, []);

  const level = useCallback(() => (rec.current ? rec.current.level() : 0), []);

  useEffect(() => () => cancel(), [cancel]);

  return { state, press, release, cancel, level, latched: isLatched };
}
